import { ApiError } from '../errors/api-error.js';

function unwrapApiPayload(apiResponse) {
    if (
        apiResponse
        && typeof apiResponse === 'object'
        && apiResponse.success === true
        && apiResponse.data !== undefined
    ) {
        return {
            data: apiResponse.data,
            message: apiResponse.message,
            pagination: apiResponse.pagination,
            uniqueValues: apiResponse.uniqueValues,
        };
    }

    return {
        data: apiResponse,
        message: undefined,
        pagination: apiResponse?.pagination,
        uniqueValues: apiResponse?.uniqueValues,
    };
}

export class AssetGroupCriticalityEvaluationService {
    constructor(api) {
        this.api = api;
    }

    async getAll(params = {}) {
        try {
            const response = await this.api.getAll(params);
            const payload = unwrapApiPayload(response);
            return {
                success: true,
                data: payload.data,
                pagination: payload.pagination || {},
                uniqueValues: payload.uniqueValues || {},
                message: payload.message,
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch evaluations');
        }
    }

    async assignExperts(assetGroupId, data) {
        try {
            const response = await this.api.assignExperts(assetGroupId, data);
            const payload = unwrapApiPayload(response);
            return { success: true, data: payload.data, message: payload.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to assign experts');
        }
    }

    async startSurvey(id, data) {
        try {
            const response = await this.api.startSurvey(id, data);
            const payload = unwrapApiPayload(response);
            return { success: true, data: payload.data, message: payload.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to start survey');
        }
    }

    async getMySurveys() {
        try {
            const response = await this.api.getMySurveys();
            const payload = unwrapApiPayload(response);
            return { success: true, data: payload.data, message: payload.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch my surveys');
        }
    }

    async getSurveyDetail(evaluationId) {
        try {
            const response = await this.api.getSurveyDetail(evaluationId);
            const payload = unwrapApiPayload(response);
            return { success: true, data: payload.data, message: payload.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch survey detail');
        }
    }

    async submitSurvey(evaluationId, data) {
        try {
            const response = await this.api.submitSurvey(evaluationId, data);
            const payload = unwrapApiPayload(response);
            return { success: true, data: payload.data, message: payload.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to submit survey');
        }
    }

    async getSummary(id) {
        try {
            const response = await this.api.getSummary(id);
            const payload = unwrapApiPayload(response);
            return { success: true, data: payload.data, message: payload.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch summary');
        }
    }

    async getEvaluationPreview(id) {
        try {
            const response = await this.api.getEvaluationPreview(id);
            const payload = unwrapApiPayload(response);
            return { success: true, data: payload.data, message: payload.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch evaluation preview');
        }
    }

    async approve(id, data) {
        try {
            const response = await this.api.approve(id, data);
            const payload = unwrapApiPayload(response);
            return { success: true, data: payload.data, message: payload.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to approve');
        }
    }

    async repeatSurvey(id) {
        try {
            const response = await this.api.repeatSurvey(id);
            const payload = unwrapApiPayload(response);
            return { success: true, data: payload.data, message: payload.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to repeat survey');
        }
    }

    async restart(id, data) {
        try {
            const response = await this.api.restart(id, data);
            const payload = unwrapApiPayload(response);
            return { success: true, data: payload.data, message: payload.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to restart evaluation');
        }
    }

    _handleError(error, fallback) {
        if (error instanceof ApiError) return error;
        const message = error?.response?.data?.error || error?.message || fallback;
        return new ApiError(message, error?.response?.status || 500, error?.response?.data);
    }
}
